import { Suspense, useEffect, useState } from 'react';
import { Formik } from 'formik';
import refreshIcon from '../assets/icons/refresh.svg';
import close from '../assets/icons/closeBlack.svg';
import add from '../assets/icons/add.svg';
import change from '../assets/icons/update.svg';
import remove from '../assets/icons/remove.svg';
import tooltip from '../assets/icons/tooltipIcon.svg';
import Button from './Button';
import GridTable from './GridTable';
import { ColumnDef, PaginationState } from '@tanstack/react-table';
import { AuditLogModel } from '../models/auditlog.model';
import { AuditActionTypes } from '../enums/enum';
import SearchableDropdown from './SearchableDropdown';
import {
	useGetAllFieldsQuery,
	useGetAuditLogsMutation,
} from '../store/apiSlices/recruitApiSlice';
import { useSearchParams } from 'react-router-dom';
import { DropdownModel } from '../models/dropdown.model';
import { toastHandler } from '../utils/toastHandler';
import { StatusCode } from '../enums/httpStatus.enum';
import { getFormattedDateString } from '../functions/dateFunction';
import { DisplayFieldNames, FieldNames } from '../constants';
import { phoneNumberWithCountryCode } from '../utils/phone-format';
import { getCurrencyFormat } from '../functions/stringFunctions';
import Tooltip from './Tooltip/Tooltip';

const AuditLog = ({ onCancel }: { onCancel: () => void }) => {
	const [searchParams] = useSearchParams();
	const recruit_id = searchParams.get('id');
	const allFields = useGetAllFieldsQuery(null);
	const [fieldNames, setFieldNames] = useState<DropdownModel[]>([]);
	const [rows, setRows] = useState<AuditLogModel[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [selectedField, setSelectedField] = useState<DropdownModel | null>(
		null,
	);
	const [updateAuditLogs, updateAuditLogsProps] = useGetAuditLogsMutation();
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});

	useEffect(() => {
		if (!allFields?.currentData?.value) return;
		const fields = allFields.currentData.value;
		const updatedFields = fields.map((field) => {
			const updatedField = getFormattedFieldType(field.value);
			return {
				...field,
				value: updatedField || '',
			};
		});
		setFieldNames([...updatedFields]);
	}, [allFields?.currentData]);

	const handleRefresh = () => {
		handleAuditLogs({
			recruit_id: recruit_id as string,
			field_name: selectedField?.id || '',
			page_number: pagination.pageIndex + 1,
			page_size: pagination.pageSize,
		});
	};

	const getFormattedFieldType = (fieldName: string) => {
		switch (fieldName) {
			case FieldNames.generalName:
				return DisplayFieldNames.generalName;
			case FieldNames.generalStageId:
				return DisplayFieldNames.generalStageId;
			case FieldNames.generalEmail:
				return DisplayFieldNames.generalEmail;
			case FieldNames.generalPhone:
				return DisplayFieldNames.generalPhone;
			case FieldNames.generalRoleId:
				return DisplayFieldNames.generalRoleId;
			case FieldNames.generalCurrentCompany:
				return DisplayFieldNames.generalCurrentCompany;
			case FieldNames.generalCity:
				return DisplayFieldNames.generalCity;
			case FieldNames.generalStateId:
				return DisplayFieldNames.generalStateId;
			case FieldNames.generalRecruitingManagerId:
				return DisplayFieldNames.generalRecruitingManagerId;
			case FieldNames.generalFirstContactedDate:
				return DisplayFieldNames.generalFirstContactedDate;
			case FieldNames.generalNmlsId:
				return DisplayFieldNames.generalNmlsId;
			case FieldNames.generalIsProducing:
				return DisplayFieldNames.generalIsProducing;
			case FieldNames.outsideRecruiterIsOutsideRecruiter:
				return DisplayFieldNames.outsideRecruiterIsOutsideRecruiter;
			case FieldNames.outsideRecruiterName:
				return DisplayFieldNames.outsideRecruiterName;
			case FieldNames.outsideRecruiterPaymentTypeId:
				return DisplayFieldNames.outsideRecruiterPaymentTypeId;
			case FieldNames.staffTotal:
				return DisplayFieldNames.staffTotal;
			case FieldNames.staffLOs:
				return DisplayFieldNames.staffLOs;
			case FieldNames.staffClosers:
				return DisplayFieldNames.staffClosers;
			case FieldNames.staffLOAs:
				return DisplayFieldNames.staffLOAs;
			case FieldNames.staffUnderwriters:
				return DisplayFieldNames.staffUnderwriters;
			case FieldNames.staffProcessors:
				return DisplayFieldNames.staffProcessors;
			case FieldNames.staffOthers:
				return DisplayFieldNames.staffOthers;
			case FieldNames.staffOfficeId:
				return DisplayFieldNames.staffOfficeId;
			case FieldNames.productionMonthlyUnits:
				return DisplayFieldNames.productionMonthlyUnits;
			case FieldNames.productionMonthlyVolume:
				return DisplayFieldNames.productionMonthlyVolume;
			case FieldNames.productionAnnualUnits:
				return DisplayFieldNames.productionAnnualUnits;
			case FieldNames.productionAnnualVolume:
				return DisplayFieldNames.productionAnnualVolume;
			case FieldNames.productionConventionalLoans:
				return DisplayFieldNames.productionConventionalLoans;
			case FieldNames.productionBonds:
				return DisplayFieldNames.productionBonds;
			case FieldNames.productionGovernmentLoans:
				return DisplayFieldNames.productionGovernmentLoans;
			case FieldNames.productionBroker:
				return DisplayFieldNames.productionBroker;
			case FieldNames.productionJumbo:
				return DisplayFieldNames.productionJumbo;
		}
	};

	const getActionType = (action: string) => {
		switch (action) {
			case AuditActionTypes.add:
				return (
					<div className="flex gap-x-1 text-rcrm-lime">
						<img src={add} />
						<p>Add</p>
					</div>
				);
			case AuditActionTypes.change:
				return (
					<div className="flex gap-x-1 text-rcrm-secondary-orange">
						<img src={change} />
						<p>Change</p>
					</div>
				);
			case AuditActionTypes.remove:
				return (
					<div className="flex gap-x-1 text-rcrm-error">
						<img src={remove} />
						<p>Remove</p>
					</div>
				);
		}
	};

	const getValues = (val: string | number) => {
		switch (selectedField?.id) {
			case FieldNames.generalPhone:
				return phoneNumberWithCountryCode(val as string);
			case FieldNames.generalFirstContactedDate:
				return getFormattedDateString(val as string, 'MM/dd/yyyy');
			case FieldNames.outsideRecruiterPaymentTypeId: {
				if (val.toString().includes('Other')) {
					return (
						<div className="flex items-center">
							<span>{val ? 'Other' : ''}</span>
							<Tooltip
								icon={tooltip}
								message={
									val
										.toString()
										.replace(/Other/g, '')
										.replace(/\((.*?)\)/g, '$1') || ''
								}
								position="right"
							/>
						</div>
					);
				} else if (val.toString().includes('Flat fee')) {
					const removedFlatFee = val
						.toString()
						.slice(8)
						.replace(/\(\$(.*?)\)/g, '$1');
					const updatedVal = (
						<div className="h-full">
							<p>Flat fee</p>
							<p>
								{'(' + getCurrencyFormat(removedFlatFee) + ')'}
							</p>
						</div>
					);
					return updatedVal;
				} else {
					const removedBps = val.toString().slice(3);
					const updatedVal = (
						<div className="h-full">
							<p>Bps</p>
							<p>{removedBps}</p>
						</div>
					);
					return updatedVal;
				}
			}
			case FieldNames.productionAnnualVolume:
				return getCurrencyFormat(val as string);
			case FieldNames.productionMonthlyVolume:
				return getCurrencyFormat(val as string);
			default:
				return val;
		}
	};

	const columns: ColumnDef<AuditLogModel>[] = [
		{
			accessorKey: 'actionType',
			cell: (info) => (
				<span className="w-[120px]">
					{getActionType(info.getValue() as string)}
				</span>
			),
			header: () => <span className="w-[120px]">Action Type</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'oldValue',
			cell: (info) => (
				<span className="w-[150px]">
					{(info.getValue() as string) || '-'}
				</span>
			),
			header: () => <span className="w-[150px]">Old Value</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'newValue',
			cell: (info) => (
				<span className="w-[150px] font-medium">
					{(info.getValue() as string) || '-'}
				</span>
			),
			header: () => <span className="w-[150px]">New Value</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'modifiedBy',
			cell: (info) => (
				<span className="w-[120px] font-medium">
					{(info.getValue() as string) || '-'}
				</span>
			),
			header: () => <span className="w-[120px]">Modified By</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'timestamp',
			cell: (info) => (
				<span className="w-[140px] font-medium">
					{(info.getValue() as string) || '-'}
				</span>
			),
			header: () => <span className="w-[140px]">Timestamp</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
	];

	const handleAuditLogs = async ({
		recruit_id,
		field_name,
		page_number,
		page_size,
	}: {
		recruit_id: string;
		field_name: string;
		page_number: number;
		page_size: number;
	}) => {
		try {
			const res = await updateAuditLogs({
				recruit_id,
				field_name: field_name || '',
				page_number: page_number,
				page_size: page_size,
			});
			if (res.data?.statusCode === StatusCode.OK) {
				const totalCount = res.data.value.total_count || 0;
				setTotalCount(totalCount);
				const newRows: AuditLogModel[] = res.data.value?.auditlogs.map(
					(val) => {
						const { rowKey, oldValue, newValue, ...rest } = val;
						const updatedNewValue = newValue
							? (getValues(newValue) as string)
							: '';
						const updatedOldValue = oldValue
							? (getValues(oldValue) as string)
							: '';
						return {
							...rest,
							timestamp:
								getFormattedDateString(
									val.timestamp,
									'HH:mm, MM/dd/yyyy',
								) || '',
							oldValue: updatedOldValue,
							newValue: updatedNewValue,
						};
					},
				);
				setRows(newRows);
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			toastHandler({ isError: true, msg: e });
		}
	};

	const handleClose = () => {
		onCancel?.();
	};

	const handlePagination = (updatedPagination: PaginationState) => {
		setPagination(updatedPagination);
		handleAuditLogs({
			recruit_id: recruit_id as string,
			field_name: selectedField?.id || '',
			page_number: updatedPagination.pageIndex + 1,
			page_size: updatedPagination.pageSize,
		});
	};

	const handleSelection = (val: DropdownModel) => {
		setSelectedField(val);
	};

	return (
		<Formik
			initialValues={{
				fields: [],
			}}
			enableReinitialize
			onSubmit={handleRefresh}
		>
			{() => (
				<div className="flex flex-col h-full">
					<div className="flex justify-between items-center">
						<div className="flex gap-2 px-2 py-3 mx-3 items-center bg-white">
							<img
								src={refreshIcon}
								className="w-4 h-4"
								alt="filter"
							></img>
							<div className="text-rcrm-title font-semibold text-xl">
								Audit Log
							</div>
						</div>
						<img
							src={close}
							className="w-5 h-5 mx-3 cursor-pointer"
							alt=""
							onClick={() => handleClose()}
						/>
					</div>
					<div className="flex flex-col px-6 py-4 bg-rcrm-primary-bg h-full gap-y-3 overflow-auto">
						<div className="flex font-semibold items-end gap-x-2">
							<div className="flex flex-grow">
								<Suspense fallback="loading...">
									<SearchableDropdown
										list={fieldNames || []}
										label="Field"
										name="fields"
										isRequired={false}
										placeholder="Select Field"
										dropdownContainer="bg-white"
										selectedItemClassName="text-gray-700 text-sm font-semibold"
										listItemClassName="text-gray-700 text-sm font-semibold"
										labelClassName="flex-grow"
										onChangeSelection={handleSelection}
										isUpperCase={false}
									/>
								</Suspense>
							</div>
							<Button
								label="Search"
								buttonStyle="bg-rcrm-primary-blue m-[2px] rounded-md"
								onButtonClick={handleRefresh}
								labelStyle="text-white h-[20px] flex justify-center items-center p-[14px]"
							/>
						</div>
						<GridTable
							rows={rows}
							columns={columns}
							pagination={pagination}
							rowCount={totalCount}
							widthClass="lg:min-w-[calc(60vw-200px)]"
							heightClass="h-[calc(100vh-260px)]"
							handlePagination={handlePagination}
							containerClass="flex-grow"
							isHeaderSticky={true}
							isPagesizeRequired={false}
						/>
					</div>
				</div>
			)}
		</Formik>
	);
};

export default AuditLog;
